import { type ReactNode } from 'react'

import { Layout, Text, Button } from '@loadsmart/miranda-react'

import type { ShipmentCharge } from '../domain/ShipmentCharges'
import { formatStopName } from '../presenters/shipmentChargePresenter'
import { AttachmentLink, AttachmentContainer, LinkIcon } from './ShipmentChargesTable.styles'

type ShipmentChargeAction = {
  actionLabel: string
  performedBy?: string
  reasonLabel?: string
  reason?: string
}

function getShipmentChargeAction(charge: ShipmentCharge): ShipmentChargeAction | undefined {
  switch (charge.status.value) {
    case 'approved':
      return {
        actionLabel: 'Approved by',
        performedBy: charge.approvedBy === 'From quote' ? undefined : charge.approvedBy,
      }
    case 'rejected':
      return {
        actionLabel: 'Rejected by',
        performedBy: charge.rejectedBy,
        reasonLabel: 'Rejection reason',
        reason: charge.rejectReason,
      }
    case 'canceled':
      return {
        actionLabel: 'Canceled by',
        performedBy: charge.canceledBy,
        reasonLabel: 'Canceling reason',
        reason: charge.cancelReason,
      }
    case 'pending':
    default:
      return undefined
  }
}

type ExpandableRowContentFieldProps = {
  label: string
  children: ReactNode
}

function ExpandableRowContentField({ label, children }: ExpandableRowContentFieldProps) {
  return (
    <Layout.Stack gap="spacing-1" align="flex-start">
      <Text variant="body-sm" color="color-text-tertiary">
        {label}
      </Text>
      {children}
    </Layout.Stack>
  )
}

type ExpandableRowContentProps = {
  charge: ShipmentCharge
  onCancelCharge: (chargeUUID: string) => void
}

export function ExpandableRowContent({ charge, onCancelCharge }: ExpandableRowContentProps) {
  const chargeAction = getShipmentChargeAction(charge)
  return (
    <Layout.Box
      padding="spacing-5"
      paddingL="spacing-10"
      style={{ display: 'block', whiteSpace: 'normal' }}
    >
      <Layout.Stack>
        <Layout.Grid gap="spacing-10">
          <Layout.Grid>
            <ExpandableRowContentField label="Stop">
              <Text
                variant="body-md"
                color={charge.stop ? 'color-text-primary' : 'color-text-placeholder'}
              >
                {formatStopName(charge.stop)}
              </Text>
            </ExpandableRowContentField>

            <AttachmentContainer>
              <ExpandableRowContentField label="Attachment">
                {charge.attachment ? (
                  <AttachmentLink
                    href={charge.attachment.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LinkIcon name="clip" width={16} height={16} />
                    <Text
                      variant="body-md-bold-underline"
                      color="color-text-link"
                      style={{ maxWidth: 'calc(100% - 20px)' }}
                    >
                      {charge.attachment.fileName}
                    </Text>
                  </AttachmentLink>
                ) : (
                  <Text variant="body-md" color="color-text-placeholder">
                    N/A
                  </Text>
                )}
              </ExpandableRowContentField>
            </AttachmentContainer>
          </Layout.Grid>

          {chargeAction?.performedBy && (
            <ExpandableRowContentField label={chargeAction.actionLabel}>
              <Text variant="body-md">{chargeAction.performedBy}</Text>
            </ExpandableRowContentField>
          )}
        </Layout.Grid>
        <Layout.Grid gap="spacing-10">
          <ExpandableRowContentField label="Description">
            <Text
              variant="body-md"
              color={charge.description ? 'color-text-primary' : 'color-text-placeholder'}
            >
              {charge.description || 'N/A'}
            </Text>
          </ExpandableRowContentField>
          {chargeAction?.reason && chargeAction.reasonLabel && (
            <ExpandableRowContentField label={chargeAction.reasonLabel}>
              <Text variant="body-md" color="color-warning-100">
                {chargeAction.reason}
              </Text>
            </ExpandableRowContentField>
          )}
        </Layout.Grid>
        <Layout.Group align="center" justify="flex-end" gap="spacing-2">
          {charge.canBeCanceled && (
            <Button
              type="button"
              variant="danger"
              size="small"
              onClick={() => {
                onCancelCharge(charge.uuid)
              }}
            >
              Cancel
            </Button>
          )}
        </Layout.Group>
      </Layout.Stack>
    </Layout.Box>
  )
}
